import type { ReactNode } from 'react';
import { X } from 'lucide-react';
import { cn } from '../utils/cn';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  footer?: ReactNode;
  width?: number | string;
  children: ReactNode;
}

export function Modal({ open, onClose, title, footer, width = 520, children }: ModalProps) {
  if (!open) return null;
  return (
    // Outer overlay is itself scrollable so the entire dialog scrolls on
    // tall content (or short viewports) instead of clipping off-screen.
    // `items-start sm:items-center` keeps the dialog top-aligned on
    // phones where landscape + soft keyboard can leave ≤ 200px height.
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 sm:items-center"
      onClick={onClose}
    >
      <div
        style={{ width }}
        className={cn(
          // `max-h-[calc(100vh-2rem)]` matches the outer p-4 (2 × 1rem).
          // flex-col + flex-1 body lets the body scroll while header +
          // footer stay pinned.
          'my-auto flex max-h-[calc(100vh-2rem)] w-full max-w-full flex-col rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)] shadow-xl',
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-center justify-between border-b border-[var(--color-border)] px-4 py-3">
          <div className="text-sm font-semibold text-[var(--color-text-primary)]">{title}</div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-[var(--color-bg-card-hover)]"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">{children}</div>
        {footer && <div className="flex shrink-0 justify-end gap-2 border-t border-[var(--color-border)] px-4 py-3">{footer}</div>}
      </div>
    </div>
  );
}

export default Modal;
